"use client"

import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { Sparkles, Link as LinkIcon } from "lucide-react"
import Link from "next/link"
import { ShareToolSection } from "./share-tool-section"
import { ReviewAccessModal } from "./review-access-modal"
import { useState } from "react"
import { getDefaultReviewYear } from "../utils"

interface CompletionScreenProps {
  email: string
  userId?: string | null
  reviewData: any
  onBack: () => void
}

export function CompletionScreen({ email, userId, reviewData, onBack }: CompletionScreenProps) {
  const [showAccessModal, setShowAccessModal] = useState(false)
  const currentYear = getDefaultReviewYear()

  return (
    <div className="text-center space-y-8">
      <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-primary/10 mb-4">
        <Sparkles className="h-10 w-10 text-primary" />
      </div>
      <h1 className="text-4xl md:text-5xl font-bold text-balance">You've Done Something Beautiful</h1>
      <p className="text-lg text-muted-foreground leading-relaxed max-w-2xl mx-auto text-pretty">
        Taking time to reflect is an act of self-respect. You've honored this year — all of it. The light and the
        shadow. The growth and the rest.
      </p>



      {/* Review Access (for anonymous users) */}
      {!userId && (
        <div className="text-center pt-4">
          <Button
            onClick={() => setShowAccessModal(true)}
            variant="outline"
            size="lg"
            className="text-base gap-2 border-primary/20 hover:border-primary/40 hover:bg-primary/5 text-primary"
          >
            <LinkIcon className="h-4 w-4" />
            Get Link to Your Review
          </Button>
        </div>
      )}

      {/* Share Tool Section */}
      <div className="pt-2">
        <ShareToolSection email={email} userId={userId || null} />
      </div>

      <Card className="p-8 text-left space-y-2 bg-card/50 backdrop-blur">
        <h3 className="text-2xl font-semibold">What happens next?</h3>
        <p className="text-muted-foreground leading-relaxed">
          Your reflections are powerful, but they're even more meaningful when they shape your future. 
          <br/> With Planami, you can transform these insights into actionable plans, visual goals and moodboard, that
          help you unlock your full potential.
        </p>

        <div className="space-y-4 pt-4">
          <div className="flex items-start gap-3">
            <div className="mt-1 h-5 w-5 rounded-full bg-primary/20 flex items-center justify-center shrink-0">
              <div className="h-2 w-2 rounded-full bg-primary" />
            </div>
            <div>
              <h4 className="font-medium mb-1">Turn reflections into goals</h4>
              <p className="text-sm text-muted-foreground">
                Your insights about energy, relationships, and growth become the foundation for meaningful goals
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="mt-1 h-5 w-5 rounded-full bg-primary/20 flex items-center justify-center shrink-0">
              <div className="h-2 w-2 rounded-full bg-primary" />
            </div>
            <div>
              <h4 className="font-medium mb-1">Track what matters</h4>
              <p className="text-sm text-muted-foreground">
                Monitor the habits and practices that truly nourish you, not just what you think you "should" do
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="mt-1 h-5 w-5 rounded-full bg-primary/20 flex items-center justify-center shrink-0">
              <div className="h-2 w-2 rounded-full bg-primary" />
            </div>
            <div>
              <h4 className="font-medium mb-1">Plan with intention</h4>
              <p className="text-sm text-muted-foreground">
                Use customizable templates designed for reflection, wellness, and authentic goal-setting
              </p>
            </div>
          </div>
        </div>
      </Card>

      <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
        <Link href="/auth/sign-up">
          <Button size="lg" className="text-base">
            Start Your 7-Day Premium Trial
          </Button>
        </Link>
        <Link href="/planner?demo=true">
          <Button size="lg" variant="outline" className="text-base bg-transparent">
            Explore the Planner
          </Button>
        </Link>
      </div>

      <p className="text-sm text-muted-foreground pt-2">
        7-day full Premium trial • No card required
      </p>

      {/* Review Access Modal */}
      {!userId && (
        <ReviewAccessModal
          open={showAccessModal}
          onOpenChange={setShowAccessModal}
          email={email}
          reviewYear={currentYear}
        />
      )}
    </div>
  )
}

